import { skipToken } from '@reduxjs/toolkit/dist/query';
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useState,
  Dispatch,
  SetStateAction,
  useMemo,
} from 'react';
import { useGetHandlingRequestsV2Query } from 'store/slices/clientV2/handlingRequests';
import { ALL, DISABLE_PAGE_LIMIT, QueryFilterOperators, Lite } from 'types/api';
import { HandlingRequestType, StockLotCRUDSchema } from 'types/inventory';
import { HandlingRequestSchema } from 'types/clientV2/stock';
import {
  warehouseStorageName,
  warehouseDefaults,
} from 'types/warehouseSettings';
import { Org } from 'types/org';
import useSnackbar from 'hooks/useSnackbar';
import useLocalStorage from 'hooks/useLocalStorage';
import useServerSideQuery from 'hooks/useServerSideQuery';
import {
  filterHandlingRequestsByTypeAndNoSplitStockLot,
  groupHandlingRequests,
  convertGroupedHandlingRequestsToObjects,
  ExpandedHandlingRequestWithGroupedStockLots,
  groupHandlingRequestsByStockLot,
} from 'views/warehouse/Kitting/utils';
import { Team } from 'types/properties';

export type WarehouseHandlingRequest = Lite<HandlingRequestSchema, 'stockLot'>;

type handleSetFiltersParams = {
  handlingRequestTypes: HandlingRequestType[];
  orgIds: Org['id'][];
  teamIds: Team['id'][];
};

type HandlingRequestsContextType = {
  handlingRequests: ExpandedHandlingRequestWithGroupedStockLots[];
  unfilteredHandlingRequests: WarehouseHandlingRequest[];
  selectedHandlingRequest: ExpandedHandlingRequestWithGroupedStockLots | null;
  handlingRequestTypesFilter: HandlingRequestType[];
  orgIdsFilter: Org['id'][];
  teamIdsFilter: Team['id'][];
  initiallyScannedStockLot: StockLotCRUDSchema | null;
  setInitiallyScannedStockLot: Dispatch<
    SetStateAction<StockLotCRUDSchema | null>
  >;
  isSelectHandlingRequestLocked: boolean;
  isFetchingHandlingRequests: boolean;
  isLoadingHandlingRequests: boolean;
  isHandlingRequestsSuccess: boolean;
  isHandlingRequestsError: boolean;
  refetchHandlingRequests: () => void;
  handleSelectedHandlingRequestChange: (id: string) => void;
  clearSelectedHandlingRequest: () => void;
  setIsSelectHandlingRequestLocked: Dispatch<SetStateAction<boolean>>;
  handleSetFilters: (params: handleSetFiltersParams) => void;
  handleCancelHandlingRequest: (id: string) => void;
  resetFilters: () => void;
};

const HandlingRequestsContext = createContext<
  HandlingRequestsContextType | undefined
>(undefined);

const handlingRequestExecutionOrder = [
  HandlingRequestType.MSL_RESET,
  HandlingRequestType.SPLIT,
  HandlingRequestType.SPLICE,
  HandlingRequestType.REEL,
  HandlingRequestType.REPACKAGE,
  HandlingRequestType.COMBINE,
];

export const HandlingRequestsProvider: FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const { dispatchErrorSnackbar } = useSnackbar();
  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);

  const [handlingRequestTypesFilter, setHandlingRequestTypesFilter] = useState<
    HandlingRequestType[]
  >(handlingRequestExecutionOrder); // Execution order also reflects all handling requests types to filter by
  const [orgIdsFilter, setOrgIdsFilter] = useState<Org['id'][]>([]);
  const [teamIdsFilter, setTeamIdsFilter] = useState<Team['id'][]>([]);

  const resetFilters = () => {
    setHandlingRequestTypesFilter(handlingRequestExecutionOrder);
    setOrgIdsFilter([]);
    setTeamIdsFilter([]);
  };

  const {
    data: baseHandlingRequestsResponse,
    isFetching: isFetchingBaseHandlingRequests,
    isLoading: isLoadingBaseHandlingRequests,
    isSuccess: isBaseHandlingRequestsSuccess,
    isError: isBaseHandlingRequestsError,
    refetch: refetchBaseHandlingRequests,
  } = useServerSideQuery<HandlingRequestSchema, WarehouseHandlingRequest>(
    useGetHandlingRequestsV2Query,
    // @ts-ignore -- Nested filter
    settings.facility
      ? {
          schema: [ALL, { stockLot: [] }],
          pageSize: DISABLE_PAGE_LIMIT,
          filters: [
            {
              field: 'requestType',
              operator: QueryFilterOperators.isAnyOf,
              value: handlingRequestExecutionOrder,
            },
            {
              field: 'stockLot.stockLocation.facility.id',
              operator: QueryFilterOperators.is,
              value: settings.facility,
            },
            {
              field: 'stockLot.stockLocation.parent',
              operator: QueryFilterOperators['='],
              value: settings.currentWorkstation?.id,
            },
            'or',
            {
              field: 'stockLot.stockLocation.parent.parent',
              operator: QueryFilterOperators['='],
              value: settings.currentWorkstation?.id,
            },
            {
              field: 'completedAt',
              operator: QueryFilterOperators.isEmpty,
              value: null,
            },
            {
              // eslint-disable-next-line no-restricted-syntax
              field: 'cancelledAt',
              operator: QueryFilterOperators.isEmpty,
              value: null,
            },
          ],
        }
      : skipToken
  );

  const baseHandlingRequests = useMemo(
    () => baseHandlingRequestsResponse?.data ?? [],
    [baseHandlingRequestsResponse?.data]
  );

  const restOfSpliceStockLots = useMemo(
    () =>
      baseHandlingRequests
        .filter((hr) =>
          [HandlingRequestType.SPLICE, HandlingRequestType.COMBINE].includes(
            hr.requestType
          )
        )
        .map((hr) => hr.splitStockLot)
        .filter(
          (splitStockLot, index, self) =>
            splitStockLot &&
            index === self.findIndex((s) => s && s === splitStockLot)
        ),
    [baseHandlingRequests]
  );

  const {
    data: restOfSpliceHandlingRequestsResponse,
    isFetching: isFetchingRestOfSpliceHandlingRequests,
    isLoading: isLoadingRestOfSpliceHandlingRequests,
    isSuccess: isRestOfSpliceHandlingRequestsSuccess,
    isError: isRestOfSpliceHandlingRequestsError,
    isUninitialized: isRestOfSpliceHandlingRequestsUninitialized,
    refetch: refetchRestOfSpliceHandlingRequests,
  } = useServerSideQuery<HandlingRequestSchema, WarehouseHandlingRequest>(
    useGetHandlingRequestsV2Query,
    restOfSpliceStockLots.length > 0
      ? {
          schema: [ALL, { stockLot: [] }],
          pageSize: DISABLE_PAGE_LIMIT,
          filters: [
            {
              field: 'requestType',
              operator: QueryFilterOperators.isAnyOf,
              value: [HandlingRequestType.SPLICE, HandlingRequestType.COMBINE],
            },
            {
              field: 'completedAt',
              operator: QueryFilterOperators.isEmpty,
              value: null,
            },
            {
              // eslint-disable-next-line no-restricted-syntax
              field: 'cancelledAt',
              operator: QueryFilterOperators.isEmpty,
              value: null,
            },
            {
              field: 'splitStockLot',
              operator: QueryFilterOperators.isAnyOf,
              value: restOfSpliceStockLots,
            },
          ],
        }
      : skipToken
  );

  const restOfSpliceHandlingRequests = useMemo(
    () => restOfSpliceHandlingRequestsResponse?.data ?? [],
    [restOfSpliceHandlingRequestsResponse?.data]
  );

  const combinedHandlingRequestsResponse = useMemo(() => {
    const all = [...baseHandlingRequests, ...restOfSpliceHandlingRequests];
    return all.filter(
      (hr, index, self) =>
        hr && index === self.findIndex((h) => h && h.id === hr.id)
    );
  }, [baseHandlingRequests, restOfSpliceHandlingRequests]);

  const [initiallyScannedStockLot, setInitiallyScannedStockLot] =
    useState<StockLotCRUDSchema | null>(null);

  const [isSelectHandlingRequestLocked, setIsSelectHandlingRequestLocked] =
    useState<boolean>(false);

  const [selectedHandlingRequest, setSelectedHandlingRequest] =
    useState<ExpandedHandlingRequestWithGroupedStockLots | null>(null);

  const handlingRequests = useMemo(() => {
    const filteredHandlingRequests =
      combinedHandlingRequestsResponse?.filter(
        (hr) =>
          handlingRequestTypesFilter.includes(
            hr.requestType as HandlingRequestType
          ) &&
          (orgIdsFilter.length === 0 || orgIdsFilter.includes(hr.org)) &&
          (teamIdsFilter.length === 0 ||
            teamIdsFilter.includes(hr.stockLot.team ?? ''))
      ) || [];

    const groupedHandlingRequestsByStockLot = groupHandlingRequestsByStockLot(
      filteredHandlingRequests
    );

    const spliceHandlingRequests =
      filterHandlingRequestsByTypeAndNoSplitStockLot(
        filteredHandlingRequests,
        HandlingRequestType.SPLICE
      );

    const combineHandlingRequests =
      filterHandlingRequestsByTypeAndNoSplitStockLot(
        filteredHandlingRequests,
        HandlingRequestType.COMBINE
      );

    const groupedSpliceHandlingRequests = groupHandlingRequests(
      spliceHandlingRequests
    );

    const groupedCombineHandlingRequests = groupHandlingRequests(
      combineHandlingRequests
    );

    const groupedSpliceHandlingRequestsObjects =
      convertGroupedHandlingRequestsToObjects(groupedSpliceHandlingRequests);

    const groupedCombineHandlingRequestsObjects =
      convertGroupedHandlingRequestsToObjects(groupedCombineHandlingRequests);

    const otherHandlingRequests =
      filteredHandlingRequests
        ?.filter(
          (hr) =>
            ![HandlingRequestType.SPLICE, HandlingRequestType.COMBINE].includes(
              hr.requestType as HandlingRequestType
            )
        )
        .map(
          (hr) =>
            ({
              ...hr,
              stockLots: [hr.stockLot],
              handlingRequests: [hr],
              ids: [],
            } as ExpandedHandlingRequestWithGroupedStockLots)
        ) || [];

    const sortedHandlingRequests = [
      ...groupedSpliceHandlingRequestsObjects,
      ...groupedCombineHandlingRequestsObjects,
      ...otherHandlingRequests,
    ].sort(
      (a, b) =>
        handlingRequestExecutionOrder.indexOf(a.requestType) -
        handlingRequestExecutionOrder.indexOf(b.requestType)
    );

    const stockLotsWithHandlingRequests = Object.values(
      groupedHandlingRequestsByStockLot
    ).map((hrs) => ({
      ...hrs[0].stockLot,
      handlingRequests: hrs,
    }));

    const finalHandlingRequests = sortedHandlingRequests.filter((request) => {
      const relatedStockLots = stockLotsWithHandlingRequests.filter(
        (stockLot) => stockLot.id === request.stockLot.id
      );

      if (relatedStockLots.length === 0) {
        return true;
      }

      const highestOrderRequest = relatedStockLots[0].handlingRequests.sort(
        (a, b) =>
          handlingRequestExecutionOrder.indexOf(a.requestType) -
          handlingRequestExecutionOrder.indexOf(b.requestType)
      )[0];

      return (
        handlingRequestExecutionOrder.indexOf(request.requestType) <=
        handlingRequestExecutionOrder.indexOf(highestOrderRequest.requestType)
      );
    });

    return finalHandlingRequests;
  }, [
    combinedHandlingRequestsResponse,
    handlingRequestTypesFilter,
    orgIdsFilter,
    teamIdsFilter,
  ]);

  // This function takes a stock lot id and finds the first grouped handling request that contains a stock lot matching that id.
  // If it finds that the request is already set, it unsets it.
  const handleSelectedHandlingRequestChange = (
    id: ExpandedHandlingRequestWithGroupedStockLots['id']
  ) => {
    if (isSelectHandlingRequestLocked) {
      dispatchErrorSnackbar(
        'You must complete the current task before selecting another'
      );
      return;
    }
    setSelectedHandlingRequest((prev) =>
      prev && prev.id === id
        ? null
        : handlingRequests.find((hr) => hr.id === id) || null
    );
  };

  const clearSelectedHandlingRequest = () => {
    setSelectedHandlingRequest(null);
  };

  const handleSetFilters = ({
    handlingRequestTypes,
    orgIds,
    teamIds,
  }: handleSetFiltersParams) => {
    setHandlingRequestTypesFilter(handlingRequestTypes);
    setOrgIdsFilter(orgIds);
    setTeamIdsFilter(teamIds);
  };

  const handleCancelHandlingRequest = async (id: string) => {
    setSelectedHandlingRequest((prev) => {
      if (!prev) return null;

      const remainingHandlingRequests = prev.handlingRequests.filter(
        (hr) => hr.id !== id
      );
      if (
        remainingHandlingRequests.length === 0 ||
        ([HandlingRequestType.SPLICE, HandlingRequestType.COMBINE].includes(
          prev.requestType
        ) &&
          remainingHandlingRequests.length <= 1)
      ) {
        return null;
      }

      const remainingStockLots = prev.stockLots.filter((lot) =>
        remainingHandlingRequests.some((hr) => hr.stockLot.id === lot.id)
      );

      return {
        ...prev,
        handlingRequests: remainingHandlingRequests,
        stockLots: remainingStockLots,
      };
    });
  };

  const refetchHandlingRequests = () => {
    refetchBaseHandlingRequests();
    if (!isRestOfSpliceHandlingRequestsUninitialized) {
      refetchRestOfSpliceHandlingRequests();
    }
  };

  return (
    <HandlingRequestsContext.Provider
      value={{
        handlingRequests,
        unfilteredHandlingRequests: combinedHandlingRequestsResponse,
        selectedHandlingRequest,
        isSelectHandlingRequestLocked,
        handlingRequestTypesFilter,
        orgIdsFilter,
        teamIdsFilter,
        isFetchingHandlingRequests:
          isFetchingBaseHandlingRequests ||
          isFetchingRestOfSpliceHandlingRequests,
        isLoadingHandlingRequests:
          isLoadingBaseHandlingRequests ||
          isLoadingRestOfSpliceHandlingRequests,
        isHandlingRequestsSuccess:
          isBaseHandlingRequestsSuccess &&
          isRestOfSpliceHandlingRequestsSuccess,
        isHandlingRequestsError:
          isBaseHandlingRequestsError || isRestOfSpliceHandlingRequestsError,
        refetchHandlingRequests,
        handleSelectedHandlingRequestChange,
        setIsSelectHandlingRequestLocked,
        handleSetFilters,
        handleCancelHandlingRequest,
        resetFilters,
        clearSelectedHandlingRequest,
        initiallyScannedStockLot,
        setInitiallyScannedStockLot,
      }}
    >
      {children}
    </HandlingRequestsContext.Provider>
  );
};

export const useHandlingRequestsContext = () => {
  const context = useContext(HandlingRequestsContext);
  if (context === undefined) {
    throw new Error(
      'useHandlingRequestsContext must be used within a HandlingRequestsProvider'
    );
  }
  return context;
};
